import React, { useState, useEffect } from 'react';

const FacebookAIAnalysis = ({ tileData, isOpen, onClose }) => {
  const [analysis, setAnalysis] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && tileData) {
      analyzeData();
    }
  }, [isOpen, tileData]);

  const analyzeData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Simulate AI analysis based on tile data
      const analysisResult = generateAIAnalysis(tileData);
      setAnalysis(analysisResult);
    } catch (err) {
      setError('Erreur lors de l\'analyse des données');
      console.error('Analysis error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const generateAIAnalysis = (data) => {
    const analysis = {
      summary: '',
      insights: [],
      recommendations: [],
      trends: [],
      concerns: [],
      opportunities: []
    };

    switch (data.type) {
      case 'analytics':
        analysis.summary = `Vos analytics Facebook montrent une performance ${data.data.trend?.includes('+') ? 'positive' : 'négative'} avec ${data.data.followers} abonnés et un taux d'engagement de ${data.data.engagement}%.`;
        
        analysis.insights = [
          `Portée de ${data.data.reach} personnes cette semaine`,
          `Taux d'engagement de ${data.data.engagement}% (${data.data.engagement > 3 ? 'excellent' : 'à améliorer'})`,
          `Croissance de ${data.data.trend} par rapport à la semaine dernière`
        ];

        analysis.recommendations = [
          data.data.engagement < 3 ? 'Augmentez l\'interaction avec votre audience' : 'Continuez votre stratégie actuelle',
          'Publiez plus de contenu engageant',
          'Répondez rapidement aux commentaires'
        ];

        if (data.data.engagement > 4) {
          analysis.opportunities.push('Excellent engagement ! Capitalisez sur ce succès');
        }
        break;

      case 'feed':
        analysis.summary = `Vos publications récentes montrent ${data.data.totalPosts} posts avec un engagement moyen de ${data.data.avgEngagement}%.`;
        
        analysis.insights = [
          `Meilleure publication: "${data.data.bestPost}"`,
          `Publication moins performante: "${data.data.worstPost}"`,
          `Engagement moyen: ${data.data.avgEngagement}%`
        ];

        analysis.recommendations = [
          'Analysez pourquoi certaines publications performent mieux',
          'Répliquez le style des publications réussies',
          'Évitez le type de contenu des publications moins performantes'
        ];
        break;

      case 'audience':
        analysis.summary = `Votre audience est principalement composée de ${data.data.gender.female}% de femmes et ${data.data.gender.male}% d'hommes, avec ${data.data.ageGroups['25-34']}% dans la tranche 25-34 ans.`;
        
        analysis.insights = [
          `Audience principalement ${data.data.ageGroups['25-34']}% de 25-34 ans`,
          `Répartition: ${data.data.gender.female}% femmes, ${data.data.gender.male}% hommes`,
          `Top villes: ${data.data.topCities.join(', ')}`,
          `Intérêts: ${data.data.interests.join(', ')}`
        ];

        analysis.recommendations = [
          'Adaptez votre contenu à votre audience principale (25-34 ans)',
          'Ciblez les villes les plus représentées',
          'Créez du contenu lié aux intérêts identifiés'
        ];
        break;

      case 'content':
        analysis.summary = `Votre contenu comprend ${data.data.videoPosts} vidéos, ${data.data.imagePosts} images et ${data.data.textPosts} publications texte.`;
        
        analysis.insights = [
          `Vidéos: ${data.data.avgVideoViews} vues en moyenne`,
          `Images: ${data.data.avgImageLikes} likes en moyenne`,
          `Mix de contenu: ${data.data.videoPosts} vidéos, ${data.data.imagePosts} images, ${data.data.textPosts} textes`
        ];

        analysis.recommendations = [
          data.data.avgVideoViews > 1000 ? 'Continuez à créer des vidéos' : 'Améliorez la qualité de vos vidéos',
          'Testez différents formats de contenu',
          'Analysez les vidéos les plus performantes'
        ];
        break;

      case 'trends':
        analysis.summary = `Vos meilleures heures de publication sont ${data.data.peakHours.join(' et ')} et vos meilleurs jours sont ${data.data.bestDays.join(' et ')}.`;
        
        analysis.insights = [
          `Heures de pointe: ${data.data.peakHours.join(', ')}`,
          `Meilleurs jours: ${data.data.bestDays.join(', ')}`,
          `Taux d'engagement: ${data.data.engagementRate}%`,
          `Croissance: ${data.data.growthRate}`
        ];

        analysis.recommendations = [
          'Publiez aux heures de pointe identifiées',
          'Planifiez vos publications pour les meilleurs jours',
          'Analysez pourquoi ces moments sont plus efficaces'
        ];
        break;

      case 'competitor':
        analysis.summary = `Vous êtes en position ${data.data.marketPosition} avec un engagement de ${data.data.avgEngagement}% vs ${data.data.competitorAvg}% pour vos concurrents.`;
        
        analysis.insights = [
          `Position: ${data.data.marketPosition} dans la région`,
          `Engagement: ${data.data.avgEngagement}% vs ${data.data.competitorAvg}% concurrents`,
          `Croissance: ${data.data.growthVsCompetitors} vs concurrents`
        ];

        analysis.recommendations = [
          'Maintenez votre avantage concurrentiel',
          'Analysez les stratégies des concurrents',
          'Continuez à innover'
        ];
        break;

      default:
        analysis.summary = 'Analyse des données en cours...';
        analysis.insights = ['Données en cours d\'analyse'];
        analysis.recommendations = ['Recommandations en cours de génération'];
    }

    return analysis;
  };

  if (!isOpen) return null;

  return (
    <div className="facebook-ai-analysis-overlay" onClick={onClose}>
      <div className="facebook-ai-analysis-container" onClick={(e) => e.stopPropagation()}>
        <div className="analysis-header">
          <div className="analysis-title">
            <div className="analysis-icon">🤖</div>
            <div>
              <h3>Analyse IA - {tileData?.title}</h3>
              <p>Interprétation intelligente de vos données Facebook</p>
            </div>
          </div>
          <button className="analysis-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="analysis-content">
          {isLoading ? (
            <div className="analysis-loading">
              <div className="loading-spinner"></div>
              <p>Analyse en cours...</p>
            </div>
          ) : error ? (
            <div className="analysis-error">
              <p>❌ {error}</p>
            </div>
          ) : analysis ? (
            <div className="analysis-results">
              <div className="analysis-summary">
                <h4>📊 Résumé</h4>
                <p>{analysis.summary}</p>
              </div>

              <div className="analysis-section">
                <h4>💡 Insights</h4>
                <ul>
                  {analysis.insights.map((insight, index) => (
                    <li key={index}>{insight}</li>
                  ))}
                </ul>
              </div>

              <div className="analysis-section">
                <h4>🎯 Recommandations</h4>
                <ul>
                  {analysis.recommendations.map((rec, index) => (
                    <li key={index}>{rec}</li>
                  ))}
                </ul>
              </div>

              {analysis.opportunities.length > 0 && (
                <div className="analysis-section opportunities">
                  <h4>🚀 Opportunités</h4>
                  <ul>
                    {analysis.opportunities.map((opp, index) => (
                      <li key={index}>{opp}</li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.concerns.length > 0 && (
                <div className="analysis-section concerns">
                  <h4>⚠️ Points d'attention</h4>
                  <ul>
                    {analysis.concerns.map((concern, index) => (
                      <li key={index}>{concern}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <div className="analysis-footer">
          <button className="analysis-action-btn" onClick={analyzeData}>
            🔄 Réanalyser
          </button>
          <button className="analysis-action-btn secondary" onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

export default FacebookAIAnalysis;
